import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import { archiveAcknowledgmentStatusQueryKey } from '@/hooks/queries/use-archive-acknowledgment-status-query';
import { driveArchiveStateQueryKey } from '@/hooks/queries/use-drive-archive-state-query';
import type { ApiErrorResponse, ApiResponse } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface ArchiveMentionRequest {
  mentionId: string;
  disclaimerAcknowledged: boolean;
  nonAffiliateDisclaimerShown?: boolean;
}

export interface ArchiveMentionResponseData {
  archiveId: string;
  status: 'IN_PROGRESS' | 'DEDUPLICATED';
  mentionId: string;
  driveFileId: string | null;
  driveFileWebViewLink: string | null;
  isDuplicate: boolean;
}

const SLOW_TOAST_DELAY_MS = 10_000;
const SLOW_TOAST_ID = 'mention-archive-in-progress';

const postArchiveMention = async ({
  mentionId,
  disclaimerAcknowledged,
  nonAffiliateDisclaimerShown,
}: ArchiveMentionRequest): Promise<ApiResponse<ArchiveMentionResponseData>> => {
  const response = await api.post<ApiResponse<ArchiveMentionResponseData>>(
    `/dashboard/mentions/${mentionId}/archive-to-drive`,
    {
      disclaimerAcknowledged,
      nonAffiliateDisclaimerShown,
    }
  );
  return response.data;
};

/**
 * POSTs to /dashboard/mentions/:id/archive-to-drive.
 *
 * Behavior:
 * - The mutation lives at the hook level, so closing the modal does not cancel it.
 * - If the request takes longer than 10s we surface a non-blocking "Uploading..."
 *   loading toast (the button still shows its inline loading state).
 * - On success: invalidates the archive-state query for that mention and shows a
 *   success toast translated via the response code.
 * - On error: translated via getApiErrorMessage and shown as an error toast.
 */
export const useArchiveMentionToDrive = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const slowToastShownRef = useRef(false);
  const slowToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSlowToast = () => {
    if (slowToastTimerRef.current) {
      clearTimeout(slowToastTimerRef.current);
      slowToastTimerRef.current = null;
    }
    if (slowToastShownRef.current) {
      toaster.dismiss(SLOW_TOAST_ID);
      slowToastShownRef.current = false;
    }
  };

  return useMutation<
    ApiResponse<ArchiveMentionResponseData>,
    AxiosError<ApiErrorResponse>,
    ArchiveMentionRequest
  >({
    mutationFn: (variables) => {
      slowToastTimerRef.current = setTimeout(() => {
        slowToastShownRef.current = true;
        toaster.create({
          id: SLOW_TOAST_ID,
          type: 'loading',
          title: t('mentions.archive.toast.in_progress_long'),
          duration: Infinity,
        });
      }, SLOW_TOAST_DELAY_MS);

      return postArchiveMention(variables);
    },
    onSuccess: (response, variables) => {
      clearSlowToast();

      const isDeduplicated = response.data?.isDuplicate === true;
      const titleKey = isDeduplicated
        ? 'mentions.archive.toast.deduplicated'
        : 'mentions.archive.toast.success';

      // Translate via response.code if present, else fall back to our static key.
      const codeKey = response.code ? `messages.${response.code}` : undefined;
      const description = codeKey
        ? t(codeKey, { defaultValue: response.message ?? '' })
        : (response.message ?? '');

      toaster.success({
        title: t(titleKey),
        description: description || undefined,
      });

      queryClient.invalidateQueries({
        queryKey: driveArchiveStateQueryKey(variables.mentionId),
      });
      // FOU-514: backend recorded the session ack as a side effect; flip
      // the cached status so subsequent clicks bypass the modal.
      queryClient.invalidateQueries({
        queryKey: archiveAcknowledgmentStatusQueryKey,
      });
    },
    onError: (error) => {
      clearSlowToast();

      const description = getApiErrorMessage(error, t, 'common.error');
      toaster.error({
        title: t('common.error'),
        description,
      });
    },
  });
};
